'use client';

import { createClient } from '@/lib/supabase/client';

export interface NewsCategory {
  id: string;
  name: string;
  slug: string;
  description: string;
  displayOrder: number;
  isActive: boolean;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;

  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '08') return true;
  }

  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }

  return false;
}

export const newsCategoryService = {
  async getAll(): Promise<NewsCategory[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { data, error } = await supabase
        .from('news_categories')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return (data || []).map((row) => ({
        id: row.id,
        name: row.name,
        slug: row.slug,
        description: row.description,
        displayOrder: row.display_order,
        isActive: row.is_active,
        createdBy: row.created_by,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      }));
    } catch (error: any) {
      console.error('Get news categories error:', error.message);
      throw error;
    }
  },

  async getActive(): Promise<NewsCategory[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('news_categories')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return [];
        }
      }

      return (data || []).map((row) => ({
        id: row.id,
        name: row.name,
        slug: row.slug,
        description: row.description,
        displayOrder: row.display_order,
        isActive: row.is_active,
        createdBy: row.created_by,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
      }));
    } catch (error: any) {
      console.error('Get active news categories error:', error.message);
      throw error;
    }
  },

  async getById(id: string): Promise<NewsCategory | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { data, error } = await supabase
        .from('news_categories')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return null;
        }
      }

      return {
        id: data.id,
        name: data.name,
        slug: data.slug,
        description: data.description,
        displayOrder: data.display_order,
        isActive: data.is_active,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      console.error('Get news category by ID error:', error.message);
      throw error;
    }
  },

  async create(category: Omit<NewsCategory, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>): Promise<NewsCategory | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { data, error } = await supabase
        .from('news_categories')
        .insert({
          name: category.name,
          slug: category.slug,
          description: category.description,
          display_order: category.displayOrder,
          is_active: category.isActive,
          created_by: user.id,
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return null;
        }
      }

      return {
        id: data.id,
        name: data.name,
        slug: data.slug,
        description: data.description,
        displayOrder: data.display_order,
        isActive: data.is_active,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      console.error('Create news category error:', error.message);
      throw error;
    }
  },

  async update(id: string, category: Partial<Omit<NewsCategory, 'id' | 'createdAt' | 'updatedAt' | 'createdBy'>>): Promise<NewsCategory | null> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const updateData: any = {};
      if (category.name !== undefined) updateData.name = category.name;
      if (category.slug !== undefined) updateData.slug = category.slug;
      if (category.description !== undefined) updateData.description = category.description;
      if (category.displayOrder !== undefined) updateData.display_order = category.displayOrder;
      if (category.isActive !== undefined) updateData.is_active = category.isActive;

      const { data, error } = await supabase
        .from('news_categories')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
          return null;
        }
      }

      return {
        id: data.id,
        name: data.name,
        slug: data.slug,
        description: data.description,
        displayOrder: data.display_order,
        isActive: data.is_active,
        createdBy: data.created_by,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      console.error('Update news category error:', error.message);
      throw error;
    }
  },

  async delete(id: string): Promise<void> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      const { error } = await supabase
        .from('news_categories')
        .delete()
        .eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Data error:', error.message);
        }
      }
    } catch (error: any) {
      console.error('Delete news category error:', error.message);
      throw error;
    }
  },

  async toggleActive(id: string, isActive: boolean): Promise<NewsCategory | null> {
    return this.update(id, { isActive });
  },

  async reorder(categories: { id: string; displayOrder: number }[]): Promise<void> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    try {
      for (const category of categories) {
        const { error } = await supabase
          .from('news_categories')
          .update({ display_order: category.displayOrder })
          .eq('id', category.id);

        if (error) {
          if (isSchemaError(error)) {
            console.error('Schema error:', error.message);
            throw error;
          } else {
            console.log('Data error:', error.message);
          }
        }
      }
    } catch (error: any) {
      console.error('Reorder news categories error:', error.message);
      throw error;
    }
  },
};